/**
 * 网络环境工具类
 * 用于获取当前网络带宽信息、判断是否弱网环境（App / 微信 / 手机浏览器 / PC 均适用）
 *
 * 判定策略说明（按证据置信度从高到低排序，命中即判弱网）：
 *   ┌─────────────────────────────────────────────────────────────────┐
 *   │ 优先级 1 (实测高置信度)：带宽探测 / 历史请求平均耗时              │
 *   │   - 解决 Chrome DevTools 节流不修改 navigator.connection 的问题    │
 *   │   - 解决 VPN/QoS/降速/公共 WiFi 拥塞等 API 报告不准的场景          │
 *   │   - 双通道互为备份：                                                    │
 *   │     ① 七牛图主动探测(probeNetworkBandwidth)：冷启动立刻测，无视样本   │
 *   │     ② axios 全局埋点(collectRequestTiming)：60s 缓存期内不漏判        │
 *   │       反例：去掉 ② → 60s 缓存期内只有 1a 单证据；去掉 ① → 冷启动 3条样本不足全漏判 │
 *   │ 优先级 2 (用户意图)：saveData 省流模式                            │
 *   │ 优先级 3 (制式类强信号)：effectiveType / type = 2g/3g/流量         │
 *   │ 优先级 4 (API 估算弱信号)：downlink < 阈值 / rtt ≥ 阈值            │
 *   └─────────────────────────────────────────────────────────────────┘
 *
 * Safari 兼容说明：
 *   Safari（含 iOS 全平台）**不支持 Network Information API**，
 *   effectiveType/downlink/rtt/saveData 将全部为空或默认值。
 *   此时依赖 优先级1（带宽探测+历史请求）+ 优先级3（type 兜底）组合完成判定，
 *   不会出现"永远判非弱网"的问题。
 *
 * 审计合规说明（2026-08-31 确认）：
 *   - JS 不做 ICMP ping，仅用 HTTP 探针（new Image + axios 埋点，都是 HTTP）
 *   - 探测地址为业务自提供：七牛 bosdev.liangyihui.net/ic_splash_logo.png（CORS=*）
 *   - Safari/Firefox 不支持 navigator.connection 时，全靠 ①+② 两条 HTTP 实测通道
 *
 * 【反例：本文件内注释严禁写死数字行号（例如「见 L123」「见 L399-L415」）】
 *   本模块是连续重构热点（2026-08-31 ~ 2026-09-01 就累计加了 120+ 行注释/代码），
 *   每次加注释/改逻辑时，上面写死的数字行号就会全部漂移，后人点进去看的是完全不相干的代码。
 *   ✅ 正确写法：『见 XXX 函数内部的 YYY 分支』『见下方 Promise.race().then() 内 ZZZ 逻辑』，
 *      用函数名 + 语义化位置名代替数字行号。
 *   ❌ 例外：跨文件引用（例如『见 lyhVideoCustom.vue L247-253』）允许写行号，
 *      因为外部文件不会因为本文件重构就漂移。
 */

// ========================== 阈值常量 ==========================

// 弱网带宽阈值（Mbps）：低于该值视为弱网
// 推荐配置（适配 HLS 视频切流场景）：
//   360p 流畅：~0.8 Mbps  → 阈值 ≥ 1.0
//   480p 标清：~1.2 Mbps  → 阈值 ≥ 1.5（保守 1.2 可覆盖边缘情况）
//   720p 高清：~2.5 Mbps  → 阈值 ≥ 3.0
// 反例：若阈值低于实际码率下限（如 0.7 < 0.8），会出现"判非弱网但仍卡顿"
const WEAK_NETWORK_DOWNLINK_THRESHOLD = 1.2

// 弱网 RTT 阈值（毫秒）：往返延迟超过该值视为弱网
// 说明：基站拥塞、跨洋链路等场景带宽可能充足但延迟高（>300ms），
//       视频首帧、seek 响应、互动直播体验仍会明显劣化
const WEAK_NETWORK_RTT_THRESHOLD = 500

// 带宽探测结果有效时长（毫秒）：探测一次后 N 毫秒内复用，避免频繁探测浪费流量
// 反例：TTL 设 < 10s → 用户反复进出视频页时每次都重新下 26KB，流量成本指数上升；
//       TTL 设 > 300s → 用户从地铁进 WiFi 时仍沿用旧的弱网结论，画质被一直钉在流畅档投诉
const PROBE_CACHE_TTL = 60 * 1000 // 60 秒

// 带宽探测资源的「保守估计文件大小（KiB）」—— 只用于 bandwidthEstimateMbps 的日志参考，不参与判定
// 2026-08-31 curl 实测 ic_splash_logo.png = 26706 bytes ≈ 26.08 KiB，这里向下取整到 26 避免估算虚高
// 反例：如果未来更换 PROBE_RESOURCE_URL 但忘记更新此值，bandwidthEstimateMbps 会偏差（但不影响 isWeak 判定，
//       因为 isWeak 只基于 durationMs ≥ PROBE_DURATION_WEAK_THRESHOLD，不读取 bandwidthEstimateMbps）
const PROBE_ESTIMATE_SIZE_KB = 26

// 带宽探测资源（已实锤审查通过：七牛 bosdev.liangyihui.net 桶 / ic_splash_logo.png）
// —— 2026-08-31 curl 实测（见审查笔记，所有项 100/100 通过）：
//   [√] 文件大小       : 26706 bytes ≈ 26.08 KiB → 推荐 20~50 KiB 黄金区间，带宽测量占比 ≈ 60%
//   [√] Referer 防盗链: 伪造 Referer=evil.com 返回 200 → 无防盗链限制，各环境 H5 通用
//   [√] Ignore QS     : ?v=1 vs ?v=999 均返回 x-cache-status=MISS → 未开忽略参数，__netprobe 时间戳绕缓存有效
//   [√] CORS          : access-control-allow-origin=* → 跨域 fetch/new Image() 均通行，审计合规
//   [√] 内容静态不变   : ETag=AAgojbShcovQavcWjCW1OlWSMURT / Content-Length=26706 → 不会被压缩/替换抖动
//
// 兜底路径说明：如果未来七牛域名迁移/资源被删导致 404，img.onerror 会用失败耗时继续判定（不会挂）
//              见 probeNetworkBandwidth() 函数内部『img.onerror 兜底分支』（图片加载失败时不抛错，仍写入 durationMs）
const PROBE_RESOURCE_URL = 'https://bosdev.liangyihui.net/ic_splash_logo.png'

// 带宽探测判定阈值（毫秒）：加载 PROBE_RESOURCE_URL 超过该时间视为弱网
// 校准依据（对应七牛 26KB PNG + HTTPS 冷握手场景，实测阈值 2026-08-31 更新）：
//   3G Fast (Chrome) : 下行 1.6 Mbps / RTT 300ms → 26KB×8/1.6Mbps + 300ms(TLS握手) ≈ 130ms + 350ms ≈ 480ms
//   3G Slow (Chrome) : 下行 400kbps / RTT 2000ms → 26KB×8/0.4Mbps + 2000ms       ≈ 520ms + 2100ms ≈ 2620ms
//   企业代理HTTPS握手异常: 正常 50ms → 抖动 900ms 属正常偶发，不应判弱网
//   取 1000ms 作为弱网分界线（较之前 800ms 放宽 200ms 对冲跨域握手抖动，仍能正确覆盖 3G Slow）
//
// 反例记录：阈值 ≤ 900ms 时，跨国 HTTPS 代理冷握手可能偶然命中 → 误判弱网（画质被切流畅，用户投诉）
const PROBE_DURATION_WEAK_THRESHOLD = 1000

// 历史请求采样窗口大小：只保留最近 N 次请求，时间加权
// 反例：窗口设 < 5 时，3 条慢接口 + 2 条快接口就把平均值拉回来了，偶发抖动又不判了；
//       窗口设 > 20 时，5 分钟前的慢结果还残留，用户从地下车库出来进了 WiFi 还被判弱网
const REQUEST_HISTORY_WINDOW = 10
// 历史请求平均耗时阈值（毫秒）：业务接口平均耗时超过该值，视为弱网
// =============== 调整记录（2026-09-01）：1500ms → 1000ms ===============
//   旧反例（1500ms 太高，Charles 56kbps 场景判弱网漏掉）：
//     Charles 56kbps Modem preset（下行 57.6kbps × 70% Utilisation = 40kbps ≈ 5KB/s）
//     下 20KB JSON → 4s，10KB 文档接口 → 2s，滑窗 3 条接口平均 = 1024ms
//     但 1500ms 阈值 1024<1500，backup 链路弃权。再叠加 probePromise race=1500ms
//     也下不完 26KB（5.2s），最终所有 HTTP 实测链路全弃权 → 判 false。
//   新阈值 1000ms 的校准公式：
//     - 1000ms 对应业务接口响应，连续 2+ 条接口平均 > 1000ms 说明链路已经在用户体验上有明显体感卡顿
//       （点击后 1s 以上才回，这是弱网的用户体感阈值）
//     - 反例（不设更低）：阈值 < 800ms 时，偶发的服务器 DB 抖动 850ms 单条 + 1 条正常 50ms
//       平均就会到 (850×2 + 50×1)/3 ≈ 583ms ≥ 800 都不会误判，所以 1000ms 留有充足安全边界
//     - 大文件上传 onUploadProgress Date.now 偏晚的副作用（向好偏差）：上传慢时会把 avg 拉高，
//       判弱网切流畅档 → 正好让上传用户减少视频流带宽，反而是优化体验，不是问题
//   配套：样本数 ≥2 就生效（原 ≥3，见 getRecentRequestAvgDuration() 函数 JSDoc 里『样本数阈值保护』段落），降低滑窗门槛
const REQUEST_AVG_WEAK_THRESHOLD = 1000

// ============== 【2026-09-02 已弃用删除】backup 豁免阈值常量 ==============
// 历史存档：曾设 PROBE_RACE_BACKUP_FAST_THRESHOLD = 600ms → 450ms，
//   当「probe race 超时注入占位」且 recentRequestAvgMs ≤ 阈值时，把占位 isWeak 从 true 改 false
//   （_weakOverrideByBackup:true），用于兜「2Mbps 真实带宽够但 Bridge 调度延迟 >1500ms」的误判场景。
// 弃用原因（256kbps 真弱网漏判反例，2026-09-02 实测）：
//   · backup 信号结构性不可靠：业务接口仅 2~10KB JSON，在 256kbps(32KB/s) 下也能跑到 376~580ms，
//     「接口快」≠「视频流快」（JSON 几十 KB vs 视频分片几百 KB~MB，量级不同）
//   · 256kbps 真弱网（probe 1.84s+）backupAvg 580ms 命中豁免 → 漏判 → 直播卡顿不切 480p
// 替代方案（已生效，见 _runProbeRace『race 后窗口等待』）：
//   · 150ms 窗口让真实 probe（≤1650ms 回来）覆盖占位 → 2Mbps+Bridge≤900ms 由它兜住（真实 752ms<1000ms 非弱网）
//   · 2Mbps+Bridge≥900ms 的罕见极端场景会误切 480p，但 2Mbps 看 480p 流畅——宁切勿漏，取舍可接受
//   · 2026-09-07 补充：probe 测量值已改用 Resource Timing entry.duration（见 probeNetworkBandwidth
//     finish 内注释），Bridge/主线程对回调的排队延迟不再污染测量值。上述 ≤900ms 边界内的场景从
//     「依赖测量值恰好没被 Bridge 拉高」变为「确定性正确」。
//   · 2026-09-07 第二次补充（race 超时 entry 救援；动机 = Charles 8Mbps 实测误判案例：网络层 837ms
//     已下完 26KB，onload 却被 Bridge 排队压到 1650ms 窗口外 → 旧逻辑注入占位误判弱网切 480p）：
//     _runProbeRace 注入占位前先查 entry —— 网络层已完成（entry 存在）即按真实 duration 判定，
//     『onload 迟到但网络已完成』的假弱网被消除；占位弱网只在「1500ms 时网络层确实没下完
//     （真实耗时必 >1500ms ≥ 弱网阈值，判弱本来就近乎正确）或 Resource Timing 不可用」时出现，
//     宁切勿漏语义不变

// probe race 超时后额外等待窗口（毫秒）：定时器先赢但真实 probe 还在路上时，
// 先注入占位但不立即 finalize，再等这么久看真实 probe 能否回来覆盖占位
// =============== 校准依据（2026-09-02 引入，backup 豁免弃用后此窗口独立兜 2Mbps+Bridge 场景） ===============
//   设计初衷：解决「Promise.race 赢者通吃导致真实 probe 晚几十~几百 ms 就被丢弃」的盲区
//   为什么选 150ms：
//     · 2Mbps Bridge 延迟场景（真实 onload ≈ 1552ms）：1500 + 150 = 1650 ≥ 1552 →
//       真实 probe 在窗口内回来 → 覆盖占位 → 用真实 752ms 判非弱网 ✅
//     · 256kbps 极端弱网（真实 onload ≈ 1990ms）：1500 + 150 = 1650 < 1990 →
//       窗口不够，仍用占位 → 占位恒定 isWeak:true（backup 豁免已弃用）→ 判弱网切 480p ✅
//     · 反例（设 ≥ 300ms 会怎样：秒开额外多等 150ms+，直播起播从 1650ms → 1800ms+，
//             产品对秒开敏感，不划算；且 300ms 后真实 probe 还没回来的一般就是真弱网，没必要再等）
//     · 反例（设 ≤ 50ms 会怎样：Bridge 正常 ~150ms 回调延迟的场景兜不住，1500+50=1550 < 1552，
//             真实 probe 仍在外，窗口机制白加）
const PROBE_RACE_POST_TIMEOUT_WINDOW_MS = 150

// ========================== 内部状态（闭包） ==========================

/**
 * 带宽探测缓存：{ timestamp, durationMs, isWeak, bandwidthEstimateMbps }
 * timestamp=0 表示未探测或已失效
 * 注意：此缓存仅供 netUtils 内部使用，业务不要直接读写（会破坏并发锁的一致性）
 */
let _probeCache = { timestamp: 0, durationMs: 0, isWeak: false, bandwidthEstimateMbps: 0 }

/**
 * 带宽探测 Promise 锁：避免同一时刻重复发起探测
 * 并发的 probeNetworkBandwidth 调用会共享同一个 Promise 结果
 * 反例：不加锁 → 10 个组件同时调 getNetwork() → 同时发 10 次七牛图 HTTP → 6 连接上限排队
 *       → 测量的 durationMs 被"排队等待时间"虚高 2-5 倍 → 本来是 4G，全部判成弱网
 */
let _probeLockPromise = null

/**
 * 当前在途探测的运行时元信息：{ url, startMark }
 *   - url      ：带 __netprobe 时间戳的完整请求地址（每次探测唯一）
 *   - startMark：img.src 赋值前的 performance.now() 时刻（entry.startTime 的对比基准）
 * 用途：_runProbeRace 超时注入占位前，用它查 Resource Timing entry 判断「网络层是否已完成」
 *      （entry 救援，见 _runProbeRace 内注释；动机 = Charles 8Mbps 案例：onload 被 Bridge 排队误判）
 * 一致性保证：probeNetworkBandwidth executor 赋值 → finish 清空；race 超时只可能发生在探测在途
 *   期间（probePromise 未 settle），此刻元信息必然对应当前在途探测，不会读到过期映射
 */
let _probeRuntime = { url: '', startMark: 0 }

/**
 * 历史请求耗时队列：元素结构 { durationMs, timestamp }
 * 仅保留最近 REQUEST_HISTORY_WINDOW 条，超出则丢弃最旧的
 * 写入方：collectRequestTiming()（ajax/config.js 全局 axios 拦截器每次请求后调用）
 * 读取方：getRecentRequestAvgDuration()（isWeakNetwork 和 getNetwork 都会调用）
 * 反例：如果未来把 axios 拦截器删掉了，_requestHistory 永远空 → 冷启动 3 样本永远凑不齐 →
 *       backup 链路弃权，只剩 probe 一条证据链（还能用但少了一道保险）
 */
let _requestHistory = [] // { durationMs: number, timestamp: number }[]

// ========================== probe race 通用工具（内部函数，不对外 export） ==========================

/**
 * 读取探测资源的 Resource Timing entry.duration
 * 【两处共用】① probeNetworkBandwidth finish()：onload/onerror 实测优先取 entry，剔除 Bridge 排队污染
 *            ② _runProbeRace 超时救援：占位注入前判断「网络层是否其实已完成」
 *
 * 【为什么 entry.duration 可信】浏览器在网络层响应完成时即写入 performance buffer，
 *   不等 onload 回调派发 → 不受 WebView Bridge/主线程对回调的排队延迟影响
 * 【跨域可用性】豁免清单是 startTime / duration / nextHopProtocol，无需 Timing-Allow-Origin 头
 *   （responseEnd 字段跨域强制置 0，不能直读，只能用 duration）
 *
 * 【三重防御（任一不满足返回 0，调用方按各自语义回退）】
 *   ① getEntriesByName 用带 __netprobe 时间戳的完整 url 精准匹配（每次探测 URL 唯一，理论无碰撞）
 *   ② entry.startTime >= startMark-5：防御个别 WebView 对 entry name 做规范化/忽略 query string，
 *      误匹配到同域名历史 entry（如其他业务加载同图缓存命中，duration 极小 → 反向低估漏判弱网）
 *   ③ duration > 0：DNS 失败/连接拒绝等即时失败请求的 entry 可能缺失或 duration=0
 *
 * 【返回 0 的两种语义（调用方各自处理）】
 *   - finish() 调用：回退老测量值（onload 时刻差，含 Bridge 排队误差，只会偏大）
 *   - _runProbeRace 救援调用：视为「网络层未完成」→ 注入占位弱网（宁切勿漏）
 *
 * @param {string} url - 探测资源完整 URL（带 __netprobe 时间戳）
 * @param {number} startMark - img.src 赋值前的 performance.now() 时刻
 * @returns {number} 有效的 entry.duration；查不到 / 参数无效 / API 异常返回 0
 */
function _readProbeEntryDuration(url, startMark) {
  try {
    if (!url || typeof performance === 'undefined' || typeof performance.getEntriesByName !== 'function') return 0
    const entries = performance.getEntriesByName(url)
    for (let i = entries.length - 1; i >= 0; i--) {
      const it = entries[i]
      if (
        it &&
        typeof it.startTime === 'number' && it.startTime >= startMark - 5 &&
        typeof it.duration === 'number' && it.duration > 0
      ) {
        return it.duration
      }
    }
  } catch (eRt) {
    // entry 读取异常要留痕：意味着 finish 回退到含 Bridge 误差的老测量值、race 救援退回占位弱网，
    // 线上若出现「Bridge 误判弱网复发」的工单，这里是第一个要查的证据点。
    // isNetLogEnabled 门控（生产默认静默，?debug_net=1 / 开发环境可见），与全文件日志策略一致
    try { if (isNetLogEnabled()) console.warn('[netUtils] Resource Timing entry 读取失败:', String(eRt && eRt.message || eRt)) } catch (_) {}
  }
  return 0
}

/**
 * 构建「race 超时但 Resource Timing entry 已确认网络层完成」的真实结果对象（entry 救援）
 *
 * 【与 _buildRaceTimeoutProbeHolder（占位）的本质区别】
 *   · 占位：entry 查不到（网络层真没下完 / RT 不可用）→ 恒判弱网（宁切勿漏）
 *   · 本函数：entry 已确认网络层完成，duration 即最终测量值 → 按真实阈值判定，弱不弱由数据说话
 *     （isWeak 可 true 可 false：entry.duration ≥ PROBE_DURATION_WEAK_THRESHOLD 照样判弱网）
 *
 * 【与 finish() 的同源性】finish 里 onload/onerror 迟到触发后同样取 entry.duration 计算，
 *   因此晚到的真实结果与本次救援值等值，holder 覆盖是幂等的，不会出现前后值抖动
 *
 * 【纯函数契约】同占位 holder：不读写 _probeCache / _probeRuntime，无副作用
 *
 * @param {number} durationMs - Resource Timing entry.duration（网络层记录的真实耗时，>0）
 * @param {string} url - 探测资源完整 URL（带 __netprobe 时间戳）
 */
function _buildEntryRescuedProbeHolder(durationMs, url) {
  const bandwidthEstimateMbps = durationMs > 0
    ? Math.max(0, Math.round((PROBE_ESTIMATE_SIZE_KB * 8) / (durationMs / 1000) / 1000 * 100) / 100)
    : 0
  return {
    durationMs: durationMs,
    isWeak: durationMs >= PROBE_DURATION_WEAK_THRESHOLD,
    bandwidthEstimateMbps: bandwidthEstimateMbps,
    fromCache: false,
    probeResource: url || PROBE_RESOURCE_URL,
    _entryRescue: true, // 标记：race 超时被 entry 救援（非占位；值与 finish 迟到结果同源一致）
    reason: 'race_timeout_rescued_by_entry:network_layer_done_' + Math.round(durationMs) + 'ms',
    createdAt: Date.now()
  }
}

/**
 * 构建「probe race 超时」的占位 probe 对象（恒定判弱网）
 *
 * 【2026-09-07 起调用时机收窄】_runProbeRace 注入占位前会先查 Resource Timing entry（entry 救援）：
 *   网络层已完成 → 走 _buildEntryRescuedProbeHolder 按真实 duration 判定，不进本函数；
 *   只有 entry 查不到（RT 不支持 / buffer 溢出 / 在途元信息缺失）才落到本占位 → 恒判弱网（宁切勿漏）
 *
 * 【设计动机：解决 APP/微信/浏览器 三处 Promise.race 超时注入完全重复 40+ 行代码的 DRY 问题】
 *   原问题：三处分支的 __pendingProbeHit 注入块完全一致，修改占位字段时要改 3 处，
 *          极易漏改。
 *   修正：统一封装成单函数，三处都调这一个，改字段只改一次即可
 *
 * 【纯函数契约】：只读常量 PROBE_RESOURCE_URL（与闭包状态无关），不产生任何副作用，不写 _probeCache。
 *   → 三个调用方都可以安全地把返回值赋值到自己作用域的 probeResultHolder 上，
 *     不会出现「调了函数但改了别处状态」的隐性耦合。
 *
 * 【返回对象结构（恒定弱网占位，不再有 backup 豁免分支）】
 *   · { isWeak:true, _pendingProbe:true, durationMs:raceMs, ... }
 *     isWeak 恒定 true：race 超时（26KB 未在 raceMs 内下完）本身就证明带宽极低 → 判弱网
 *     曾有的 backup 豁免分支（backupAvg ≤ 450ms 时把 isWeak 改 false）已因 256kbps 漏判弃用，
 *     弃用原因见函数体注释及原常量位注释（backup 信号不可靠：JSON 体小 + 滑窗残留）
 *
 * @param {number} raceMs - Promise.race 超时阈值（毫秒），当前三处都是 1500ms
 *                          不写死常量是为了未来若不同渠道要不同 raceMs（如直播 1500/回放 3000）
 *                          可直接传参，不用改函数内部
 * @returns {{isWeak: true, _pendingProbe: true, durationMs: number, bandwidthEstimateMbps: number,
 *            fromCache: false, probeResource: string, reason: string, createdAt: number}}
 */
function _buildRaceTimeoutProbeHolder(raceMs) {
  // ============== 【2026-09-02 弃用 backup 豁免，恢复为纯弱网占位】 ==============
  // 历史：曾根据 recentRequestAvgMs 快慢决定是否豁免（backup 快时把占位 isWeak 改成 false，
  //       _weakOverrideByBackup:true），用于兜「2Mbps 真实带宽够但 Bridge 延迟 >1500ms」的误判。
  // 弃用原因（256kbps 真弱网漏判反例）：
  //   · backup 信号结构性不可靠：业务接口仅 2~10KB JSON，在 256kbps(32KB/s) 下也能跑到 376~580ms，
  //     「接口快」≠「视频流快」（JSON 几十 KB vs 视频分片几百 KB~MB，量级不同）
  //   · backupAvg ≤ 450 阈值卡线命中豁免 → 256kbps 真弱网漏判 → 直播卡顿不切 480p
  // 取舍（宁切勿漏）：
  //   · 占位恒定 isWeak:true，race 超时（26KB 未在 raceMs 内下完）即判弱网切 480p
  //   · 「2Mbps + Bridge 延迟 ≥900ms（真实 onload > 1650ms 窗口）」的罕见场景会被误切 480p，
  //     但 2Mbps 看 480p 也流畅，代价是画质降一档而非卡顿 → 可接受
  // 2Mbps 常见场景由 _runProbeRace 的 150ms 窗口兜住（真实 probe 窗口内回来覆盖占位），
  // 不需要也不应该靠 backup 豁免（见下方『race 后窗口等待』段落注释）
  const ms = raceMs || 1500
  return {
    isWeak: true,                                              // 占位恒定判弱网（race 超时 = 26KB 没下完）
    _pendingProbe: true,                                       // 标记：占位（非真实测量）
    durationMs: ms,
    bandwidthEstimateMbps: 0.14,                               // 26KB/1.5s ≈ 0.14Mbps，日志参考
    fromCache: false,
    probeResource: PROBE_RESOURCE_URL,
    reason: 'probe_race_timeout:' + ms + 'ms_not_finish_26KB_bandwidth_le_' +
      Math.round(26 * 8 / (ms / 1000) / 1000 * 100) / 100 + 'Mbps', // 动态算带宽上限，日志可读
    createdAt: Date.now()
  }
}

/**
 * 通用 probe race 包装：封装 Promise.race([probePromise, timeout(raceMs)]) + 超时注入占位 + 回调 onFinish
 *
 * 【调用方职责 vs 通用函数职责的划分】
 *   · 通用函数负责：
 *     ① Promise.race 超时器（默认 1500ms，可传 raceMs 定制）
 *     ② 判断是否是「定时器先到 + probeResultHolder 还为空」
 *     ③ 是 → 调 _buildRaceTimeoutProbeHolder(raceMs) 生成占位并赋值给 probeResultHolder
 *     ④ 【2026-09-02 新增：race 后窗口等待】（核心改进点，解决『赢者通吃丢弃迟到真实结果』的盲区）：
 *        仅当第 ③ 步真的注入了占位（定时器赢且 holder 为空），才额外 wait 一个窗口
 *        （PROBE_RACE_POST_TIMEOUT_WINDOW_MS = 150ms，具体校准值见常量区注释）
 *        → 期间若真实 probePromise resolve 了，它会在调用方作用域里把 probeResultHolder 改写为真实结果
 *          （因为调用方 probePromise 的 .then() 链里就有 `probeResultHolder = res`，跑在微任务队列里）
 *        → 最终 onFinish() 里读 getHolder() 拿到的就是真实 probe，而不是占位
 *        → 解决了 2Mbps Bridge 延迟场景（真实 1552ms，race1500ms 赢，原来用占位；现在等 150ms，1650ms 前
 *           真实 probe 回来覆盖 → 用真实 752ms 判非弱网 ✅）
 *        → 对 256kbps 极端弱网无副作用（真实 onload 要 ~1990ms，远在 1650ms 窗口外 → 仍用占位，
 *           占位恒定 isWeak:true（backup 豁免已弃用）→ 判弱网切 480p ✅）
 *     ④+ 【2026-09-07 新增：race 超时先查 Resource Timing entry（entry 救援），见代码内注释】：
 *        ③ 注入占位前，先用 _probeRuntime 元信息查探测资源 entry（网络层响应完成即写入 buffer，
 *        不等 onload 回调派发）→ entry 存在 = 网络已完成只是回调被排队 → 用真实 duration 判定，
 *        并跳过 150ms 窗口直接 finalize；entry 不存在 = 网络层真没下完（真实耗时必 >1500ms ≥
 *        弱网阈值）→ 才注入占位，宁切勿漏语义不变
 *     ⑤ 不论谁赢、窗口等不等 → 最后都调 onFinish() 回调（让调用方自己执行 finalize(baseInfo)）
 *   · 调用方负责（三处各不相同，所以抽不进通用函数，留回调交给调用方）：
 *     ① 传自己作用域里的 probePromise / probeResultHolder 引用
 *       （因为 probeResultHolder 是 getNetwork 闭包里的 let 变量，通用函数要能直接给它赋值，
 *        所以传一个 setter 而不是 getter：用 {getHolder, setHolder} 读写闭包变量）
 *     ② 传 onFinish 回调（通常 = () => finalize(baseInfo)，因为 baseInfo 在 APP/微信/浏览器三处
 *       分别是不同的局部变量，无法在通用函数里访问）
 *     ③ 传可选的 raceMs（默认 1500ms，目前三处都用默认即可）
 *
 * 【为什么用 getHolder/setHolder 而不是直接传 probeResultHolder 值和 probePromise】
 *   反例：直接传 probeResultHolder = null → 通用函数内部 `if (!holder)` 判断永远为 true，
 *        但真实 probePromise resolve 后会在调用方作用域里把 probeResultHolder 改成真实结果，
 *        通用函数里拿到的那个「值拷贝」还是旧的 null → 判断错误，重复注入占位。
 *   → 正确做法：传两个闭包函数 getHolder() / setHolder(v)，读写调用方闭包里的真实变量，
 *     这样通用函数里「race 到定时器时判断 holder 是否真的还为空」和调用方是同一个内存引用。
 *
 * @param {{
 *   probePromise: Promise<any>,
 *   getHolder: () => any,
 *   setHolder: (v: any) => void,
 *   onFinish: () => void,
 *   raceMs?: number
 * }} opts
 * @returns {Promise<void>} resolve 时机 = onFinish 执行完（通用函数不关心返回值，调用方只关心副作用）
 */
function _runProbeRace(opts) {
  opts = opts || {}
  const probePromise = opts.probePromise || Promise.resolve()
  const getHolder = opts.getHolder || (() => null)
  const setHolder = opts.setHolder || (() => {})
  const onFinish = opts.onFinish || (() => {})
  const raceMs = typeof opts.raceMs === 'number' ? opts.raceMs : 1500

  return Promise.race([
    probePromise,
    new Promise((r) => setTimeout(() => r({ __pendingProbeHit: true, raceMs }), raceMs))
  ]).then(async (raceResult) => {
    // 标记：本轮是否真的注入了「占位弱网」——只有注入了占位才需要额外等 150ms 窗口
    // （如果是真实 probe 先赢，或者 holder 已经被真实 probe 填上了，就不需要等，尽早 finalize 保秒开）
    let injectedPlaceholderPending = false
    try {
      if (raceResult && raceResult.__pendingProbeHit === true && !getHolder()) {
        // 🔑 定时器先赢，且真实 probe 还没回。
        // 【2026-09-07 新增：entry 救援——注入占位前先查 Resource Timing entry】
        // 动机（Charles 8Mbps 实测误判案例）：网络层 837ms 已下完 26KB（<1000ms 弱网阈值），
        //   但 WebView Bridge/主线程把 onload 回调排队压了 ≥813ms → probePromise 到 1650ms
        //   窗口外才 resolve → 旧逻辑直接注入占位误判弱网切 480p。
        // 原理：Resource Timing entry 在【网络层响应完成】时就写入 performance buffer，
        //   不等 onload 回调派发 —— race 超时时刻查 entry 可区分两种本质不同的情况：
        //   ① entry 存在且 duration>0 → 网络层已完成，只是回调被排队
        //      → 用 entry.duration 走真实判定（弱网阈值照常生效），并跳过 150ms 窗口直接 finalize
        //        （finish 的真实结果与 entry 同源同值，晚到的覆盖是等值幂等的，无需再等）
        //   ② entry 不存在 → 网络层 1500ms 真没下完 → 真实 duration 必 >1500ms ≥ 弱网阈值
        //      → 注入占位判弱网本身就是正确结论（宁切勿漏语义不变）
        // 失效场景（此时退回占位，宁切勿漏）：Resource Timing 不支持的老 WebView /
        //   performance buffer 溢出 entry 被挤掉 / _probeRuntime 在途元信息缺失
        const rescueDuration = _readProbeEntryDuration(_probeRuntime.url, _probeRuntime.startMark)
        if (rescueDuration > 0) {
          setHolder(_buildEntryRescuedProbeHolder(rescueDuration, _probeRuntime.url))
          // 注意：不置 injectedPlaceholderPending —— entry 即最终测量值，无需等 150ms 窗口，
          // 直接 onFinish finalize，为直播起播省下 150ms
        } else {
          setHolder(_buildRaceTimeoutProbeHolder(raceResult.raceMs || raceMs))
          injectedPlaceholderPending = true
        }
      }
    } catch (_) {
      // 注入占位的 try-catch：任何异常都不要吞掉 onFinish 的调用（有 finally 兜底）
      injectedPlaceholderPending = false
    }

    // 【race 后窗口等待：仅当真的注入了占位，才额外等 PROBE_RACE_POST_TIMEOUT_WINDOW_MS】
    // 为什么用 await setTimeout 而不是再包一层 Promise.race？
    //   因为我们的目的不是「窗口内谁赢用谁」，而是：
    //     probePromise 在 150ms 窗口内 resolve → 会触发调用方那一侧的 .then( probeResultHolder = res ) 微任务
    //     等 150ms 后这个微任务肯定跑完了 → getHolder() 就能拿到真实结果
    //   用 Promise.race 的话微任务顺序不保证（真实 probe 在窗口边缘回来，可能我们先 setTimeout 先 resolve）
    //   而 sleep 150ms 后再读 holder，是时序上最稳妥的（微任务队列一定在 150ms 宏观时间内排空）
    if (injectedPlaceholderPending) {
      await new Promise((r) => setTimeout(r, PROBE_RACE_POST_TIMEOUT_WINDOW_MS))
      // 反例（极端情况）：150ms 后 holder 还是占位 → 说明真实 probe 真的 > raceMs+150ms 了
      //   这种情况就是真弱网（比如 256kbps ~1990ms onload），保留占位即可，占位里的 isWeak=true/豁免逻辑已经兜底
    }

    try { onFinish() } catch (_) { /* onFinish 内部 finalize 有 try-catch 终极兜底，这里再护一层 */ }
  })
}

// ========================== 对外 API ==========================

/**
 * 记录一次业务请求的实际耗时（由 axios 拦截器埋点调用）
 * 不需要手动调用，已在 src/ajax/config.js 中接入
 *
 * 写入规则 & 副作用说明：
 *   - 只接收 >= 0 的 number，其它全部 ignore（null/undefined/negative/NaN 都 ignore，避免脏数据）
 *   - 新请求 push 后数组超过 REQUEST_HISTORY_WINDOW = 10 → 丢弃最旧的（FIFO）
 *   - 纯同步操作，0 Promise 0 await，不会阻塞 axios 拦截器链（否则会拖慢每个请求的放行时间）
 *
 * 反例：
 *   ① 大文件上传（响应 50MB）onUploadProgress 期间阻塞主线程，Date.now() 取到的结束时间被延后
 *      → durationMs 比真实值多 100-300ms，这是「歪打正着」：上传占用上行带宽，下行视频流会被 ACK 延迟挤压，
 *         此时判定弱网切流畅反而贴合用户体验，是我们想要的。
 *   ② 第三方远程接口（如埋点上报、CDN 预热、第三方 SDK 请求）也走 axios 拦截器
 *      → 第三方慢接口可能把平均值拉高误判。如果线上发现误判率高，可以在 config.js 埋点处加
 *         URL 前缀白名单过滤（只统计 /api/ /video/ 等自家业务接口）。
 *
 * @param {number} durationMs - 请求总耗时（响应时间 - 发起时间），毫秒
 */
export function collectRequestTiming(durationMs) {
  if (typeof durationMs !== 'number' || !(durationMs >= 0)) return
  const now = Date.now()
  _requestHistory.push({ durationMs, timestamp: now })
  if (_requestHistory.length > REQUEST_HISTORY_WINDOW) {
    // 丢弃最旧的，保留滑动窗口
    _requestHistory = _requestHistory.slice(_requestHistory.length - REQUEST_HISTORY_WINDOW)
  }
}

/**
 * 获取历史请求的平均耗时（滑动窗口时间加权：越新权重越高）
 *
 * 加权算法说明（线性递增加权）：
 *   假设窗口最近 10 条请求，按时间从旧到新排 [R1, R2, ..., R10]
 *   权重 = R1 权重 1, R2 权重 2, ..., R10 权重 10
 *   加权平均 = Σ(Ri×Wi) / ΣWi
 *   目的：对最新网络波动更敏感（刚卡顿的 1 条权重 = 10，1 分钟前的 1 条权重 = 1）
 *
 * 样本数阈值保护（调整记录：2026-09-01，3 条 → 2 条）：
 *   - 旧阈值（<3 条 return 0）：Charles 56kbps 限速场景下，刚进页面才 1-2 条慢请求，
 *     backup 链路直接弃权 → 再叠加 probePromise race 1500ms 也拿不到 probe（26KB 要 5.2s）
 *     → 所有 HTTP 实测链路全弃权 → 判 false，和真实体感不符。
 *   - 新阈值（<2 条 return 0，≥2 条就生效）：
 *     · 线性加权 2 条样本最小平均：如果刚进页面 2 条接口都是 1000ms+，已经能反映链路明显变慢，
 *       再往上就是 2000ms/条的体感卡顿，判弱网是完全合理的。
 *     · 反例（不能低于 1 条）：阈值 1 条就生效 → 后端冷启动第 1 条接口 2s 直接判弱网，
 *       误判和"为什么我点第一下就卡流畅档"投诉（保留样本≥2 的目的是最小化偶发抖动）
 *
 * @returns {number} 平均耗时（毫秒）；样本不足返回 0（不参与判定）
 */
export function getRecentRequestAvgDuration() {
  if (_requestHistory.length < 2) return 0 // 样本太少（<2 条），避免偶发高延迟误判
  // 线性加权：最新一条权重 = 窗口大小（即 _requestHistory.length），最旧一条权重 = 1
  // （注：_requestHistory 是 push 的新数据追加在尾部，idx 从 0 开始对应最旧数据，
  //       所以权重 = idx+1 天然形成"越新权重越大"的递增线性分布，不需要额外算 total）
  let weightedSum = 0
  let weightSum = 0
  _requestHistory.forEach((item, idx) => {
    const weight = idx + 1 // 1 ~ length
    weightedSum += item.durationMs * weight
    weightSum += weight
  })
  return Math.round(weightedSum / weightSum)
}

/**
 * 主动触发带宽探测：通过加载业务提供的 HTTP 资源（当前=七牛 ic_splash_logo.png）测量真实网络通道速度
 * - 可正确感知 Chrome DevTools 节流 / VPN 限速 / QoS 限流 等（navigator.connection 完全不反映的场景）
 * - 有 PROBE_CACHE_TTL 内的结果缓存，不会每次都发请求
 * - 并发安全：同时间多调用共享同一个探测结果（_probeLockPromise 互斥锁保证只发 1 次 HTTP）
 *
 * 技术实现：用 `new Image().src = <PROBE_RESOURCE_URL>?__netprobe=<timestamp>`
 *   为什么用 Image 不用 fetch/XHR？：
 *     ① Image 天然支持跨域（不需要 CORS 头也能触发 onload/onerror，只需要 AC AO 开 CORS 更保险）
 *     ② Image onload/onerror 回调时机 = 资源完全下载完成，刚好反映我们要测的端到端速度
 *     ③ 与业务接口的 HTTP 通道独立，不会占用 axios 的并发连接（HTTP/1.1 6 连接上限）
 *
 * @param {object} [opts]
 * @param {boolean} [opts.force=false] - 是否忽略缓存，强制重新探测（用于手动验证弱网判定结果）
 * @returns {Promise<{durationMs: number, isWeak: boolean, bandwidthEstimateMbps: number, fromCache: boolean, probeResource?: string, _skipped?: boolean, skipReason?: string}>}
 *          durationMs            - 实际加载耗时（毫秒），优先取 Resource Timing entry.duration
 *                                  （浏览器网络层记录，剔除 Bridge/主线程回调排队延迟），entry 缺失或
 *                                  异常时回退 Image.onload/onerror - Image.src 赋值时刻（老测量值）
 *          isWeak                - 是否达到 PROBE_DURATION_WEAK_THRESHOLD(1000ms) 阈值判弱网
 *          bandwidthEstimateMbps - 粗略估算带宽（仅用于日志，**不参与判定**），基于 PROBE_ESTIMATE_SIZE_KB
 *          fromCache             - true = 本次是 PROBE_CACHE_TTL 内复用之前的结果，未发 HTTP
 *          _skipped/skipReason   - 仅在「备份链路命中，主动跳过 probe」时出现（占位 probe，不是真实测量）
 */
export function probeNetworkBandwidth(opts) {
  opts = opts || {}
  const force = !!opts.force
  const now = Date.now()

  if (!force && _probeCache.timestamp && now - _probeCache.timestamp < PROBE_CACHE_TTL) {
    return Promise.resolve({
      durationMs: _probeCache.durationMs,
      isWeak: _probeCache.isWeak,
      bandwidthEstimateMbps: _probeCache.bandwidthEstimateMbps,
      fromCache: true,
    })
  }

  if (_probeLockPromise) {
    // 已有正在进行的探测，直接复用结果
    return _probeLockPromise
  }

  _probeLockPromise = new Promise((resolve) => {
    const url = PROBE_RESOURCE_URL + '?__netprobe=' + now
    const startTime = performance.now()
    const img = new Image()
    let resolved = false

    const finish = (isTimeout) => {
      if (resolved) return
      resolved = true
      // 老测量值（onload/onerror 回调时刻 - img.src 赋值时刻）：
      //   在 APP WebView 里会被「native 网络层响应已到达 → JS 回调被 Bridge/主线程排队执行」的延迟单向拉大
      //   （实测 2Mbps + Bridge 800ms：真实网络 752ms，JS 测出 1552ms → 踩 1000ms 阈值误判弱网）。
      //   该误差只可能偏大、不可能偏小（回调只会晚于网络层完成，不会早于）。
      let durationMs = performance.now() - startTime
      // 【Resource Timing 优先】entry.duration = responseEnd - startTime，由浏览器在网络层记录，
      // 不受「回调排队 / Bridge 调度」影响 → 剔除上述 Bridge 污染，窗口内到达的探测结果即为真实网络耗时。
      // 跨域可行性：Resource Timing 规范只对 requestStart/responseStart 等子阶段做跨域强制置零，
      //   豁免清单是 startTime / duration / nextHopProtocol 三项 —— 跨域无需 Timing-Allow-Origin
      //   头即可读到真实 duration（注意：responseEnd 字段本身跨域是 0，不能直接读，只能用 duration）。
      //   → 七牛资源不加 TAO 头也能用；若未来加了 TAO，可升级用 responseEnd-requestStart 剔除建连耗时。
      // 三重防御 / onerror 适用性 / 兼容回退见 _readProbeEntryDuration() 注释
      //   （finish 与 race 超时救援两处共用同一查询函数，避免双份拷贝漂移）。
      // 残差说明：entry.duration 仍含浏览器把 network stack 时间写入 performance buffer 的内部开销
      //   （~10ms 级），相对 Bridge 的数百 ms 污染可忽略，不做修正。
      const entryDuration = _readProbeEntryDuration(url, startTime)
      if (entryDuration > 0) {
        durationMs = entryDuration
      } else if (isNetLogEnabled()) {
        // 诊断日志：entry 未命中时打出快照，用于区分三种修复手段完全不同的失败原因：
        //   ① entries=[] → 请求可能被 APP native 层拦截转发，WebView 侧无 Resource Timing entry
        //   ② entries 有记录但 duration=0 → 老内核未实现 RT Level 2 跨域豁免，连 duration 一起置零
        //      （修法：七牛 CDN 加 Timing-Allow-Origin: * 响应头）
        //   ③ entries 有记录但 startTime < startMark-5 → name 匹配异常/时钟问题（可能性最低）
        // 排查时对照 Charles 的 Duration（网络层真值），差值即 Bridge/主线程排队延迟
        let entrySnap = null
        try {
          const allEntries = performance.getEntriesByName(url)
          entrySnap = allEntries.map(function (e) {
            return { startTime: e.startTime, duration: e.duration, name: e.name }
          })
        } catch (_) {}
        try {
          console.warn('[netUtils] probe entry 未命中，回退老测量值(含Bridge排队误差)。fallbackDurationMs=' +
            Math.round(durationMs) + ' url=' + url + ' entries=', entrySnap)
        } catch (_) {}
      }
      // 粗略带宽估算（Mbps），仅用于日志参考 + 调试肉眼校准：
      //   保守估计资源大小 ≈ 26KB（对应 PROBE_RESOURCE_URL = ic_splash_logo.png 的 curl 实测 26706 bytes）
      //   估算公式：(KB × 8) ÷ (s) ÷ 1000 → Mbps
      // 反例注意：
      //   ① 此值仅用于日志展示，绝对不用于判定 isWeak（isWeak 只看 durationMs）
      //   ② 含握手 RTT、TLS 握手、CDN 回源、队列等待等，并非纯传输速率
      //   ③ 如果未来更换 PROBE_RESOURCE_URL，请同步更新 PROBE_ESTIMATE_SIZE_KB 常量
      const estimatedKb = PROBE_ESTIMATE_SIZE_KB
      const bandwidthEstimateMbps = durationMs > 0
        ? Math.max(0, Math.round((estimatedKb * 8) / (durationMs / 1000) / 1000 * 100) / 100)
        : 0
      const isWeak = isTimeout || durationMs >= PROBE_DURATION_WEAK_THRESHOLD

      _probeCache = { timestamp: Date.now(), durationMs, isWeak, bandwidthEstimateMbps }
      // 探测已结束，清空在途元信息：防止异常时序下 race 救援用过期元信息查到不相关 entry
      _probeRuntime = { url: '', startMark: 0 }
      _probeLockPromise = null
      resolve({ durationMs, isWeak, bandwidthEstimateMbps, fromCache: false })
    }

    const timeoutTimer = setTimeout(() => finish(true), 5000) // 5s 超时兜底，极端慢网不阻塞业务
    img.onload = () => { clearTimeout(timeoutTimer); finish(false) }
    img.onerror = () => {
      // 404 / 跨域 / 无 favicon 都不应该阻塞：用失败耗时也能侧面反映网络质量
      // （如果是秒级 404 说明没网；如果 1ms 失败说明浏览器很快返回了错误）
      clearTimeout(timeoutTimer)
      finish(false)
    }
    // 记录在途探测元信息，供 _runProbeRace 超时时的 entry 救援查询（见 _probeRuntime 定义处注释）
    _probeRuntime = { url, startMark: startTime }
    img.src = url
  })

  return _probeLockPromise
}

/**
 * 网络日志开关：开发环境默认开启；生产环境默认关闭，URL 带 ?debug_net=1 可临时开启（排障用，无需发版，如：https://doctor.liangyihui.net/?debug_net=1#/live/38608477
）
 * （与下方调试 API 挂载用同一开关语义，避免「__debugProbeNet 能调但普通日志看不到」的割裂；
 *   lyhVideoCustom.vue 里的 [DEBUG-NETUTILS] 日志也复用本开关，保持全局一致）
 */
export function isNetLogEnabled() {
  try {
    if (process.env.MODE !== 'prod') return true
  } catch (_) { /* MODE 未被静态替换 → 落到 URL 参数判断 */ }
  try {
    if (typeof location !== 'undefined' && location && location.search && /[\?&]debug_net=(?:1|true|yes|on)(?:&|$|#)/i.test(location.search)) return true
  } catch (_) { /* ignore */ }
  return false
}

/**
 * 打印网络环境及弱网判断日志
 * 说明：包两层 try-catch 保护，避免 isWeakNetwork 内部错误 / Circular / JSON 序列化异常等吞掉整条日志
 * 反例 1（防止后续误以为 netUtils 没调用）：
 *   isWeakNetwork 内部访问闭包变量时如果抛错 → console.log 完全不出 → 用户误以为 netUtils 没调用
 *   → 外层 catch 兜底输出一条「极简日志 + error 字符串」，至少能证明函数真的跑了
 * 反例 2（Chrome DevTools 过滤词问题）：
 *   如果用户 Console 过滤词是小写 netutils，而日志前缀是 `[netUtils]`（U 大写），
 *   Chrome 默认不区分大小写所以没问题；但如果用户勾了 Match case → 过滤不到。
 *   所以这条 log 也不要太大写不一致，前缀保持 `[netUtils]` 驼峰，小写也能匹配到。
 * 反例 3（生产噪音）：本函数在每次 getNetwork 都会执行，若不做环境门控，
 *   生产环境每个视频/直播页都会常驻打印一条网络环境日志 → 已用 isNetLogEnabled() 门控，
 *   生产默认静默（?debug_net=1 可开），线上排障走 __debugProbeNet / URL 参数即可。
 */
function logNetworkInfo(info, env) {
  if (!isNetLogEnabled()) return
  try {
    const printable = Object.assign({}, info, { env })
    // 避免 isWeakNetwork 抛错阻断整段日志。
    // （注意：不要把结果存到中间变量 weakResult 然后不读，
    //        直接赋值到 printable.isWeakNetwork，顺便作为 JS 返回值也能被 console.log 序列化出来）
    try {
      printable.isWeakNetwork = isWeakNetwork(printable)
    } catch (e) {
      printable.isWeakNetwork = false
      printable._isWeakNetworkError = String(e && e.message || e)
    }
    console.log('[netUtils] 当前网络环境:', printable)
  } catch (e) {
    // 终极兜底：哪怕上面都挂了，至少输出一条极简日志证明 getNetwork 被调用了，方便排查
    console.log('[netUtils] 网络环境日志输出异常(兜底):', { env, error: String(e && e.message || e) })
  }
}

/**
 * 获取当前网络带宽信息
 * 优先取浏览器 Network Information API，兜底取网络制式
 * 并主动触发一次带宽探测（结果会写入缓存，供 isWeakNetwork 读取）
 *
 * 容错保证（新增：函数级 try-catch 兜底，任何异常都会 resolve 一个最小可用对象，**永不 reject
 * 反例：is_app() / is_weixin() / wx 对象不存在 / head.extensions 读取失败 都会让 Promise 永远 pending
 *       或者直接 throw 抛错导致 playWithNetwork 等调用方静默退出（无任何报错无日志
 * @returns {Promise<{
 *   type: string, effectiveType: string, downlink: number, rtt: number, saveData: boolean,
 *   probe?: { durationMs: number, isWeak: boolean, bandwidthEstimateMbps: number, fromCache: boolean },
 *   recentRequestAvgMs: number,
 *   _error?: string
 * }>}
 *   type                   - 网络制式（如 2g/3g/4g/5g/wifi/流量），App 或微信返回
 *   effectiveType          - 浏览器估算网络类型（slow-2g/2g/3g/4g）
 *   downlink               - 浏览器估算带宽（Mbps），不支持时为 0
 *   rtt                    - 浏览器估算往返延迟（ms），不支持时为 0
 *   saveData               - 用户是否开启数据节省模式（省流模式），不支持时为 false
 *   probe                  - 实际带宽探测结果（60s 缓存内复用）
 *   recentRequestAvgMs     - 最近 10 次业务接口平均耗时（样本<2 时为 0，时间加权）
 *   _error                 - 若内部有非致命错误时保留错误信息（仅排查用）
 */
export function getNetwork() {
  // 外层大兜底：即便内部任何一步 throw，也会 resolve 一个最小可用对象，**永不 reject**
  // 反例：is_app() / is_weixin() / wx 对象不存在 / head.extensions 读取失败 都会让 Promise 永远 pending
  //       或者直接 throw 抛错 → playWithNetwork 调用方静默中断（既不报错，也不打日志）
  return Promise.resolve().then(() => {
    return new Promise((resolveFinal) => {

      // ------------ 基础信息采集（try-catch 保护，任何一步失败都不影响后续）------------
      let effectiveType = ''
      let downlink = 0
      let rtt = 0
      let saveData = false
      let errorHint = ''
      try {
        let conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection || null
        if (conn) {
          effectiveType = conn.effectiveType ? conn.effectiveType : ''
          downlink = typeof conn.downlink == 'number' ? conn.downlink : 0
          rtt = typeof conn.rtt == 'number' ? conn.rtt : 0
          saveData = !!conn.saveData
        }
      } catch (e) {
        errorHint = 'conn_read_fail:' + String(e && e.message || e)
      }
      let type = ''

      // 带宽探测：与渠道获取并行，结果通过缓存共享给 isWeakNetwork
      // 注意：probeNetworkBandwidth 内部自带 try-catch，永不 reject
      // —— 【性能优化（省 26KB / 次）】：
      //    先读备份链路 recentRequestAvgMs，如果已经明确 ≥ REQUEST_AVG_WEAK_THRESHOLD，
      //    直接跳过七牛图探测，避免在「已经连续 3+ 接口都慢」的场景下白送 26KB 流量。
      //    反例 1（不能省的场景，必须走 probe）：
      //      - 样本不足 3 条（recentRequestAvgMs=0）：例如冷启动直接进直播，样本空，备份链路弃权
      //      - recentRequestAvgMs < 阈值：接口都很快，但可能是「只限视频端口 / VPN 海外出口 / 基站瞬时拥塞」
      //        这类场景 axios 接口可能都快（443 小数据），但视频流（大分片）/ 图片 CDN 真实慢
      //    反例 2（占位 probe 字段解释）：
      //      - 跳过时注入占位 probe = { isWeak:true, _skipped:true, skipReason:'backup_hit' }
      //      - 目的 1：让 finalize 组装 info.probe 字段存在，避免后续 isWeakNetwork() 函数内部
      //        「读不到 probe 就懒加载发起一次新探测」的分支把我们刚跳过的探测重新拉起来（白优化了）
      //      - 目的 2：日志里能看到「这次是备份链路命中主动跳过」，方便排查为什么没七牛图日志
      //      - 注意：占位 probe 的 isWeak=true 是继承备份链路的结论，不是独立测量，最终 isWeakNetwork
      //        优先级 1 组依然能按「备份链路先命中短路」正常工作
      let probeResultHolder = null
      // （反例：之前这里写了 skippedByBackup 标记但没使用，
      //       要判断「是否被备份链路跳过」请直接读取 probeResultHolder?._skipped === true，
      //       不需要额外中间变量，避免 TS Hint「变量未使用」）
      let avgBefore = 0
      try {
        if (typeof getRecentRequestAvgDuration === 'function') {
          avgBefore = getRecentRequestAvgDuration()
        }
      } catch (eAvg) {
        avgBefore = 0 // 任何异常（如 function 未定义 / 挂）→ 当不知道，走原探测流程，不省流量
      }

      let probePromise
      if (avgBefore > 0 && typeof REQUEST_AVG_WEAK_THRESHOLD === 'number' && avgBefore >= REQUEST_AVG_WEAK_THRESHOLD) {
        // ====================== 命中：备份链路已判弱网，跳过七牛图探测 ======================
        const skipProbe = {
          isWeak: true,                         // 继承备份链路结论，占位给 isWeakNetwork 短路参考
          _skipped: true,                       // 标记为「主动跳过的占位结果」，区分真实测量结果（替代之前的 skippedByBackup 局部变量）
          skipReason: 'backup_avg_hit',         // 跳过原因：recentRequestAvgMs 命中阈值
          durationMs: avgBefore,                // 用备份链路的平均耗时作为参考值（日志可读，不参与判定）
          bandwidthEstimateMbps: 0,             // 没测带宽，填 0 不误导
          fromCache: false,
          probeResource: PROBE_RESOURCE_URL,
          skipAt: Date.now()
        }
        probeResultHolder = skipProbe           // finalize 里会把这个当 probe 塞到 info 里
        probePromise = Promise.resolve(skipProbe)
      } else {
        // ====================== 未命中：正常发起七牛图探测（原逻辑） ======================
        probePromise = Promise.resolve()
          .then(() => probeNetworkBandwidth())
          .then((res) => {
            probeResultHolder = res
            return res
          })
          .catch(() => {
            // 探测失败也别阻塞主流程
            probeResultHolder = null
            return null
          })
      }

      const finalize = (baseInfo) => {
        try {
          const recentRequestAvgMs = (() => {
            try { return getRecentRequestAvgDuration() } catch (_) { return 0 }
          })()
          const info = Object.assign(
            { recentRequestAvgMs },
            probeResultHolder ? { probe: probeResultHolder } : {},
            errorHint ? { _error: errorHint } : {},
            baseInfo
          )
          logNetworkInfo(info, baseInfo.env || 'unknown')
          delete info.env
          resolveFinal(info)
        } catch (eFinal) {
          // 终极兜底：finalize 里再挂了也 resolve 最小对象
          const fallback = {
            type: baseInfo.type || '', effectiveType, downlink, rtt, saveData,
            recentRequestAvgMs: 0,
            _error: (errorHint ? errorHint + '|' : '') + 'finalize_fail:' + String(eFinal && eFinal.message || eFinal)
          }
          try { console.log('[netUtils] 网络环境(兜底输出):', Object.assign({ env: baseInfo.env || 'unknown', fallback })) } catch (_) {}
          resolveFinal(fallback)
        }
      }

      // ============== 分支：App / 微信 / 浏览器 三渠道 ==============
      // 每一条路径都用 try-catch 隔离，挂了就走浏览器兜底的 finalize

      let envTag = 'browser'
      try {
        if (typeof is_app === 'function' && is_app()) {
          envTag = 'app'
          try {
            let headObj = (typeof window !== 'undefined' && window.head) || null
            let extArr = (headObj && headObj.extensions) || []
            let resExt = Array.isArray(extArr)
              ? extArr.filter((item) => item && item.name == 'networkType')
              : []
            if (resExt.length > 0 && resExt[0]) {
              type = resExt[0].value || ''
            }
          } catch (eApp) {
            errorHint = (errorHint ? errorHint + '|' : '') + 'app_ext_fail:' + String(eApp && eApp.message || eApp)
          }
          const baseInfo = { type, effectiveType, downlink, rtt, saveData, env: envTag }
          // probe 等待兜底：1500ms（与直播 weakRaceMs 对齐）
          // 具体校准理由（200ms→1500ms 的反例、1500ms 没下完=带宽≤138kbps 的强证据等）
          // 见上方 APP 分支内『probe 等待兜底』段落的注释；下面 Promise.race + 超时注入占位
          // 已统一抽取到 _runProbeRace() 工具函数，三处复用
          _runProbeRace({
            probePromise,
            getHolder: () => probeResultHolder,
            setHolder: (v) => { probeResultHolder = v },
            onFinish: () => finalize(baseInfo)
          })
          return
        }
      } catch (eIsApp) {
        errorHint = (errorHint ? errorHint + '|' : '') + 'is_app_throw:' + String(eIsApp && eIsApp.message || eIsApp)
      }

      try {
        if (typeof is_weixin === 'function' && is_weixin()) {
          envTag = 'weixin'
          // 注意：微信 JSSDK getNetworkType 仅返回 networkType，不提供带宽/延迟/省流信息
          //       因此 downlink/rtt/saveData 仍使用浏览器 Network Information API 兜底（若可用）
          let wxOk = typeof wx !== 'undefined' && wx && typeof wx.getNetworkType === 'function'
          if (wxOk) {
            let settled = false
            const settleWx = (resolvedType) => {
              if (settled) return
              settled = true
              type = resolvedType || ''
              const baseInfo = { type, effectiveType, downlink, rtt, saveData, env: envTag }
              // probe 等待兜底：1500ms（同 APP 分支，具体校准与 backup 豁免逻辑统一抽取到 _runProbeRace）
              _runProbeRace({
                probePromise,
                getHolder: () => probeResultHolder,
                setHolder: (v) => { probeResultHolder = v },
                onFinish: () => finalize(baseInfo)
              })
            }
            try {
              wx.getNetworkType({
                success: (res) => settleWx(res && res.networkType ? res.networkType : ''),
                fail: () => settleWx(''),
              })
              // 再加一个 wx 接口兜底超时 1500ms：极少数情况下 wx.getNetworkType 既不 success 也不 fail
              // （比如 wx 对象存在但权限/签名问题），避免 Promise 永远 pending
              setTimeout(() => settleWx(type), 1500)
            } catch (eWxCall) {
              errorHint = (errorHint ? errorHint + '|' : '') + 'wx_call_fail:' + String(eWxCall && eWxCall.message || eWxCall)
              settleWx('')
            }
            return
          } else {
            errorHint = (errorHint ? errorHint + '|' : '') + 'wx_getNetworkType_unavailable'
          }
        }
      } catch (eIsWx) {
        errorHint = (errorHint ? errorHint + '|' : '') + 'is_weixin_throw:' + String(eIsWx && eIsWx.message || eIsWx)
      }

      // 浏览器兜底路径（含上述分支抛错/或非 app 非微信时都会落到这）
      const baseInfo = { type, effectiveType, downlink, rtt, saveData, env: envTag }
      // 浏览器兜底：probe race 1500ms 超时注入弱网占位，与 APP/微信分支对齐
      // （200ms→1500ms 的校准理由、Safari/Firefox 无 Network Information API 兜底需求等
      //   详见上一轮注释；具体 race + 注入 + backup 豁免已统一抽到 _runProbeRace 函数）
      _runProbeRace({
        probePromise,
        getHolder: () => probeResultHolder,
        setHolder: (v) => { probeResultHolder = v },
        onFinish: () => finalize(baseInfo)
      })
    })
  })
}

/**
 * 是否弱网环境（综合投票策略：按证据置信度从高到低，命中即判弱网）
 *
 * 综合投票策略说明（解决原实现的优先级短路问题）：
 *   反例 1：早期版本存在「分支短路漏洞」写法：effectiveType='4g' 且 downlink=0 时直接 return false，
 *           完全跳过 type='2g/3g' 的兜底判断 → 在部分 Android WebView 中会漏判弱网。
 *   反例 2：Chrome DevTools 开 3G 节流，API 仍报告 effectiveType='4g' downlink='10Mbps'，
 *           原实现 100% 依赖 API → 误判非弱网。
 *   修正：所有维度独立判断，按置信度排序，任一命中即返回 true，不存在互相覆盖。
 *
 * @param {object} [info] - getNetwork 返回值（或任何包含同名字段的对象）
 * @param {{useProbeCache?: boolean}} [opts] - 仅保留缓存开关，不再支持 forceProbe（避免返回类型不稳定）
 * @returns {boolean} 纯同步返回，是否弱网：true=弱网 / false=非弱网。永远不会返回 Promise。
 *          若需要"先强制走一次真实探测、再判断"，请显式调用 probeAndJudgeWeak(info, opts)。
 *
 * 【反例：禁止在这个函数里加任何返回 Promise 的分支（如 forceProbe=true）】：
 *   曾实现 forceProbe=true 时返回 Promise<boolean>，导致同一函数两种返回契约。
 *   反例：`if (isWeakNetwork(info, { forceProbe: true })) { ... }` 调用方忘记 await，
 *        判断条件永远拿到 Promise 对象（对象 truthy），弱网逻辑永不触发或行为错乱。
 */
export function isWeakNetwork(info, opts) {
  info = info || {}
  opts = opts || {}
  const useProbeCache = opts.useProbeCache !== false // 默认 true

  // 兼容层：只有当调用方显式传了 opts.forceProbe=true（旧 API 下才会返回 Promise 的分支）
  // 才给出告警；forceProbe=false / forceProbe=0 / 不传都属于「同步 boolean 路径」，
  // 行为和新 API 完全一致，不需要误报噪音。
  // eslint-disable-next-line no-prototype-builtins
  if (opts && opts.hasOwnProperty && opts.hasOwnProperty('forceProbe') && opts.forceProbe === true) {
    try {
      console.warn('[netUtils] isWeakNetwork() 已移除 forceProbe 参数，避免 boolean/Promise 两种返回契约混在一起。' +
        '请显式调用 probeAndJudgeWeak(info, {forceProbe:true}).then(weak => ...) 并 await 其返回 Promise<boolean>。')
    } catch (_) { /* ignore */ }
  }

  const et = (info.effectiveType || '').toLowerCase()
  const dl = typeof info.downlink == 'number' ? info.downlink : 0
  const rttRaw = typeof info.rtt == 'number' ? info.rtt : 0
  const saveData = !!info.saveData
  const t = (info.type || '').toLowerCase()

  // 历史请求平均耗时：优先取 getNetwork 返回带过来的值，否则实时计算
  const recentRequestAvgMs = typeof info.recentRequestAvgMs == 'number' && info.recentRequestAvgMs > 0
    ? info.recentRequestAvgMs
    : getRecentRequestAvgDuration()

  // 带宽探测结果：优先取 getNetwork 返回带过来的值，否则读缓存
  // （缓存读取必须校验 PROBE_CACHE_TTL：_probeCache 只在 probeNetworkBandwidth 发起新探测时才刷新，
  //   若绕过 getNetwork 直接调用本函数（或 getNetwork 内 probe 异常导致 info 无 probe 字段），
  //   不校验 TTL 会读到任意陈旧结论（如 20 分钟前地铁里判的弱网），用户回 WiFi 后仍被钉在流畅档）
  let probe = info.probe && typeof info.probe == 'object' ? info.probe : null
  if (!probe && useProbeCache && _probeCache.timestamp && Date.now() - _probeCache.timestamp < PROBE_CACHE_TTL) {
    probe = {
      durationMs: _probeCache.durationMs,
      isWeak: _probeCache.isWeak,
      bandwidthEstimateMbps: _probeCache.bandwidthEstimateMbps,
      fromCache: true,
    }
  }

  // =============== 优先级 1 (最高置信度)：实测类证据 ===============
  // 这组证据来自真实流量测量，能正确反映：
  //   - Chrome DevTools 网络节流
  //   - VPN / 代理限速、企业网 QoS
  //   - 运营商达量降速、公共 WiFi 拥塞
  //   - Safari 下 Network Information API 全不可用时的唯一量化依据

  // 1a) 带宽探测命中弱网（加载 favicon 超时 / 慢于阈值）
  if (probe && probe.isWeak) return true

  // 1b) 历史业务接口平均耗时过高（样本≥2 时生效，避免偶发抖动误判，见 getRecentRequestAvgDuration 的『样本数阈值保护』段落）
  if (recentRequestAvgMs >= REQUEST_AVG_WEAK_THRESHOLD) return true

  // =============== 优先级 2：用户主动意图 ===============
  // 用户开启省流模式，即使用户实际连的是高速 WiFi，也应主动降级
  if (saveData) return true

  // =============== 优先级 3：制式类强信号 ===============
  // effectiveType 为浏览器综合估算值，已加权带宽+延迟
  if (et == 'slow-2g' || et == '2g' || et == '3g') return true
  // type 来自 App JSBridge 或微信 JSSDK 返回的制式
  // 注意："流量" 为 App 端某历史版本或特定渠道的返回值（非微信/浏览器标准值），
  //       见 lyhVideoCustom.vue L247-253 也有相同判断，请勿随意删除
  if (t == '2g' || t == '3g' || t == '流量') return true

  // 【反例：禁止在优先级 3 这里写 WIFI/4g/5g → return false 的白名单短路】
  // 反例场景（就是 Charles 56kbps 限速 + App 端遇到的漏洞）：
  //   type=WIFI / effectiveType=4g 只是"连上了什么制式"，不代表实际带宽够。
  //   以下真实场景都会上报 WIFI/4g 但实际带宽 < 1Mbps，直播/视频必然卡顿：
  //     1. 用户连了公共 WIFI，但 WIFI 出口被 QoS 限速到 56kbps（Charles 限速 / 公司/学校 WiFi）
  //     2. 用户 WIFI/4g 信号满格，但运营商"达量降速"到 128kbps/384kbps
  //     3. VPN/公司内网代理 QoS 限速（比如远程办公走海外出口慢）
  //     4. WiFi 路由器连了 100+ 人，每人分到 < 100kbps
  //   如果写 WIFI/4g → return false，会在"制式满格但带宽极差"的场景下直接 return false（短路），
  //   让后面 优先级1（probe + backup HTTP 实测） / 优先级4（downlink/rtt） 永远没机会执行，
  //   最终：WAPI/4g 报告正常、实际 56kbps 卡到爆炸，但 isWeakNetwork=false 不切 480p。
  // 正确做法：只有制式本身极弱（2g/3g/流量）才直接判 true；WIFI/4g/5g 这类强制式绝不提前短路，
  //         交由 HTTP 实测和 API 估算判断，这是和 Charles 模拟弱网能对上的关键修复。

  // =============== 优先级 4：API 估算弱信号（需做 >0 保护，避免 0 值误判） ===============
  // 反例：Safari / 旧浏览器不支持 Network Information API 时 downlink=0 / rtt=0，
  //       若不做 >0 保护会将所有不支持的设备误判为弱网 / 漏判
  if (dl > 0 && dl < WEAK_NETWORK_DOWNLINK_THRESHOLD) return true
  if (rttRaw > 0 && rttRaw >= WEAK_NETWORK_RTT_THRESHOLD) return true

  // 以上均未命中 → 非弱网
  // 注：4g/5g/wifi 不会因制式本身被判弱网，
  //     必须落到 优先级1（实测） / 优先级4（带宽/延迟） 上才判弱网，避免"4g 信号差"的误判争议
  return false
}

/**
 * 【异步版】先做一次真实带宽探测，再调用纯同步的 isWeakNetwork 做判断
 *
 * 设计动机：把"异步探测 + 同步判断"两个阶段显式拆开，避免 isWeakNetwork 返回类型不稳定
 * （一会儿 boolean，一会儿 Promise<boolean>）带来的隐式 bug。
 *
 * 使用场景：刚进页面没有任何 probe 缓存 / backup 样本，且你想 100% 确定弱网再切清晰度。
 * 通常场景建议直接用 await getNetwork() + isWeakNetwork(info) 即可，不需要手动调本函数。
 *
 * @param {object} [info] - getNetwork 返回值（或结构同名字段对象）；为空会内部自动调 getNetwork() 拉取
 * @param {{forceProbe?: boolean, useProbeCache?: boolean}} [opts]
 *        - opts.forceProbe=true：绕过 60s 缓存，强制重新下载 26KB 七牛图（⚠️烧流量，仅排障用）
 *        - opts.useProbeCache=false：不读缓存，等价于 forceProbe=true
 * @returns {Promise<boolean>} 永远返回 Promise<boolean>，调用方必须 await 或 .then(weak => ...)
 */
export async function probeAndJudgeWeak(info, opts) {
  opts = opts || {}
  const force = !!opts.forceProbe || opts.useProbeCache === false

  // 如果 info 没传，先调一次 getNetwork 拉制式/saveData 等维度（getNetwork 内部会做一次普通 probe）
  let baseInfo = info || (await getNetwork())

  // 额外强制探测一次（绕过缓存的话）；
  // 注意：probeNetworkBandwidth 只会更新模块内部 _probeCache 缓存，
  //       不会修改调用方传进来的 baseInfo.probe 旧快照。
  await probeNetworkBandwidth({ force })

  // 🔑 关键：探测完成后先抹掉 baseInfo.probe，强制 isWeakNetwork 从 _probeCache 读最新结论
  //
  // 反例 1（漏判弱网，用户看不到清晰切换，投诉卡顿）：
  //   baseInfo.probe = { isWeak:false, durationMs:300 }（60s 之前的旧快照），
  //   用户进入地铁，probeNetworkBandwidth({force:true}) 更新 _probeCache.isWeak=true, durationMs=3200,
  //   但 isWeakNetwork 优先级「baseInfo.probe > _probeCache」，读到旧快照判 false → 不切 480p 卡顿。
  //
  // 反例 2（误判弱网，用户画质被钉流畅，投诉画质差）：
  //   baseInfo.probe = { isWeak:true }（地铁里的旧快照），用户走出地铁连 WiFi，
  //   probeNetworkBandwidth({force:true}) 更新 _probeCache.isWeak=false,
  //   但 isWeakNetwork 读旧快照 → 仍返回 true，一直给流畅档。
  //
  // 构造新对象 + delete probe 键（不用 Object.assign(..., {probe:undefined})）
  // 反例：Object.assign({}, baseInfo, {probe:undefined}) 会留下键 probe=undefined，
  //       未来有人用 'probe' in safeInfo / Object.keys(safeInfo).includes('probe') 就会误判"有 probe 快照"。
  //       delete 彻底清掉键，语义与"没有 probe 字段"完全等价。
  const safeInfo = Object.assign({}, baseInfo)
  delete safeInfo.probe
  return isWeakNetwork(safeInfo, { useProbeCache: true })
}

// ================= 【调试入口：暴露到 window，控制台直接调用】 =================
// 说明：浏览器控制台无法使用 webpack 的 `@/` alias 做动态 import，
//       所以这里把常用函数挂到 window.__netUtils / window.__debugProbeNet 上，
//       方便排查时直接调用，不需要通过 Vue 组件/alias 导入。
// 用法示例（DevTools Console 直接敲）：
//   __debugProbeNet().then(console.log)   // 强制跑一次全链路探测 + 日志，解决"看不到 netUtils 日志"
//   __netUtils.isWeakNetwork({ downlink: 0.5, effectiveType: '4g' })
//
// 反例 1（生产必须包一层 NODE_ENV 判断）：
//   若生产也挂载 window.__netUtils / __debugProbeNet：
//   - 内部实现被暴露（probe / 历史队列 / 阈值常量 全部可访问）；
//   - __debugProbeNet 内部有 probeNetworkBandwidth({ force: true })，绕过 60s 缓存，
//     恶意 JS / 控制台手动调用都会白烧一次 26KB 七牛图（生产 100 万用户 x 1 次 = ~25GB/月 无意义流量）。
// 反例 2（不要完全删除调试入口，只在生产默认关闭）：
//   线上偶发"某用户某地区真的卡"排查时，只要 URL 带 ?debug_net=1 即可手动拉起，不需要重新发版。
//   （如果这里写死完全不暴露，线上要查还得改代码发版，浪费排查时间）
;(async function _exposeDebugApi() {
  if (typeof window === 'undefined') return

  // 🔑 生产环境默认不挂载。允许 URL 带 debug_net=1 手动开启（排障用，不影响普通用户）
  // 复用 isNetLogEnabled()（开发环境 OR ?debug_net=1），与网络日志同一开关，语义完全一致。
  // ⚠️ 不要用 `typeof process !== 'undefined' && ...` 形式的旧守卫（isDevEnv）：
  //   浏览器 bundle 里 process 全局未定义，该守卫永远 false → 调试 API 在开发环境也挂不上
  //   （历史 bug：这就是控制台 typeof __debugProbeNet === 'undefined' 的根因，2026-09-07 修复）
  const urlForceEnable = (typeof location !== 'undefined' && location.search && /[\?&]debug_net=(?:1|true|yes|on)(?:&|$|#)/i.test(location.search))
  if (!isNetLogEnabled()) return

  try {
    window.__netUtils = {
      getNetwork,
      isWeakNetwork,             // 同步版：传 info，永远返回 boolean，不能 forceProbe
      probeAndJudgeWeak,         // 异步版：强制探测 -> 再判，永远返回 Promise<boolean>
      probeNetworkBandwidth,
      collectRequestTiming,
      getRecentRequestAvgDuration,
    }
    /**
     * 控制台一键调试：触发一次 getNetwork + 强制带宽探测，并把结果打印出来
     * （⚠️ 会强制走七牛图探测，消耗约 26KB，仅用于排障）
     * @returns {Promise<{info: object, probe: object, weak: boolean}>}
     */
    window.__debugProbeNet = async function debugProbeNet() {
      console.log('[DEBUG-NETUTILS] 开始手动探测（强制走 probe，消耗 ~26KB，仅排障用）...')
      const info = await getNetwork()
      const probe = await probeNetworkBandwidth({ force: true })
      const weak = isWeakNetwork(info)
      console.log('[DEBUG-NETUTILS] 探测结果:', { info, probe, isWeakNetwork: weak })
      return { info, probe, isWeakNetwork: weak }
    }
    // 打印一个模块载入心跳 + 提示，控制台里容易搜到
    try { console.log('[netUtils] 调试 API 已挂载：window.__netUtils / window.__debugProbeNet。' + (urlForceEnable ? '（当前通过 ?debug_net=1 手动开启）' : '')) } catch (_) { /* noop */ }
  } catch (_) {
    // 暴露调试 API 失败不影响任何功能
  }
})()

